import { apiRequest, ApiError } from '@/lib/api/client';
import { getAuthToken } from '@/lib/api/token-store';
import { hiddenMapToBody, normalizeClinicVisibilityPayload } from '@/lib/clinic-visibility';

function createAuthHeaders(token, headers = {}) {
  const normalizedToken = String(token || getAuthToken() || '').trim();
  if (!normalizedToken) {
    throw new ApiError('authorization token is required', 401);
  }
  return {
    Authorization: `Bearer ${normalizedToken}`,
    ...headers,
  };
}

function normalizeWhatsappNumbers(list) {
  if (!Array.isArray(list)) return [];
  const out = [];
  for (const item of list) {
    const botId = String(item?.botId || '').trim();
    if (!botId) continue;
    const label = String(item?.label || '').trim() || botId;
    out.push({ botId, label });
  }
  return out;
}

function normalizeMessagesSettingsPayload(payload) {
  const botAck = payload?.botAck && typeof payload.botAck === 'object' ? payload.botAck : {};
  const patientReply =
    payload?.patientReply && typeof payload.patientReply === 'object' ? payload.patientReply : {};
  return {
    tenantId: String(payload?.tenantId || ''),
    threadGroups: Array.isArray(payload?.threadGroups) ? payload.threadGroups : [],
    botAck: {
      thresholdMinutes: Number.isFinite(botAck.thresholdMinutes) ? botAck.thresholdMinutes : null,
      maxHours: Number.isFinite(botAck.maxHours) ? botAck.maxHours : null,
    },
    // Separate from botAck since the two sections age out on very different schedules; null on
    // an older backend that has no such key.
    patientReply: {
      thresholdMinutes: Number.isFinite(patientReply.thresholdMinutes)
        ? patientReply.thresholdMinutes
        : null,
      maxHours: Number.isFinite(patientReply.maxHours) ? patientReply.maxHours : null,
    },
    canEdit: Boolean(payload?.canEdit),
    // Configured WhatsApp business numbers ([{botId,label}], gateway order). Drives the thread-list
    // "Number" filter and the new-chat send-from picker. Empty when multi-number is off.
    whatsappNumbers: normalizeWhatsappNumbers(payload?.whatsappNumbers),
    raw: payload,
  };
}

/**
 * Tenant-wide shared Messages settings (thread groups + the two attention windows) from the
 * global_settings collection. Readable by every authenticated staff user.
 * GET /api/settings/messages → { tenantId, threadGroups, botAck, patientReply, canEdit }
 *
 * canEdit reflects the server-side eligibility gate (same userType list as internal Chats);
 * use it to render the editors read-only — the PUT is enforced server-side regardless.
 */
export async function fetchMessagesSettings({ token } = {}) {
  const payload = await apiRequest('/api/settings/messages', {
    method: 'GET',
    headers: createAuthHeaders(token),
  });
  return normalizeMessagesSettingsPayload(payload);
}

/**
 * Update the shared Messages settings. Partial: only the provided keys are written.
 * PUT /api/settings/messages  body { threadGroups?: [{id,name,colorId}],
 *   botAck?: {thresholdMinutes, maxHours}, patientReply?: {thresholdMinutes, maxHours} }
 *
 * Requires an eligible userType (403 otherwise). Returns the full effective settings (same
 * shape as fetchMessagesSettings) so callers can hydrate local state from the response.
 */
export async function putMessagesSettings({ token, threadGroups, botAck, patientReply } = {}) {
  const body = {};
  if (Array.isArray(threadGroups)) {
    body.threadGroups = threadGroups;
  }
  if (botAck && typeof botAck === 'object') {
    body.botAck = {
      thresholdMinutes: Number(botAck.thresholdMinutes) || 0,
      maxHours: Number(botAck.maxHours) || 0,
    };
  }
  if (patientReply && typeof patientReply === 'object') {
    body.patientReply = {
      thresholdMinutes: Number(patientReply.thresholdMinutes) || 0,
      maxHours: Number(patientReply.maxHours) || 0,
    };
  }
  if (!('threadGroups' in body) && !('botAck' in body) && !('patientReply' in body)) {
    throw new ApiError('no settings provided', 400);
  }

  const payload = await apiRequest('/api/settings/messages', {
    method: 'PUT',
    headers: createAuthHeaders(token, { 'Content-Type': 'application/json' }),
    body: JSON.stringify(body),
  });
  return normalizeMessagesSettingsPayload(payload);
}

function normalizeSessionLockPayload(payload) {
  return {
    // Minutes of inactivity before the app locks every profile on the device; 0 disables
    // the auto-lock. Server default is 10.
    idleLockMinutes: Number.isFinite(payload?.idleLockMinutes) ? payload.idleLockMinutes : 10,
    canEdit: Boolean(payload?.canEdit),
    raw: payload,
  };
}

/**
 * Tenant-wide session-lock settings (idle auto-lock timeout) from global_settings.
 * GET /api/settings/session-lock → { idleLockMinutes, canEdit } (0 = disabled).
 */
export async function fetchSessionLockSettings({ token } = {}) {
  const payload = await apiRequest('/api/settings/session-lock', {
    method: 'GET',
    headers: createAuthHeaders(token),
  });
  return normalizeSessionLockPayload(payload);
}

/**
 * Update the session-lock settings. PUT /api/settings/session-lock
 * body { idleLockMinutes: 0..1440 }. Requires the settings permission (403 otherwise).
 */
export async function putSessionLockSettings({ token, idleLockMinutes } = {}) {
  const minutes = Number(idleLockMinutes);
  if (!Number.isFinite(minutes) || minutes < 0 || minutes > 1440) {
    throw new ApiError('idleLockMinutes must be between 0 and 1440', 400);
  }
  const payload = await apiRequest('/api/settings/session-lock', {
    method: 'PUT',
    headers: createAuthHeaders(token, { 'Content-Type': 'application/json' }),
    body: JSON.stringify({ idleLockMinutes: Math.round(minutes) }),
  });
  return normalizeSessionLockPayload(payload);
}

function normalizeAppointmentConfirmationPayload(payload) {
  const rawTemplates =
    payload?.templates && typeof payload.templates === 'object' && !Array.isArray(payload.templates)
      ? payload.templates
      : {};
  const templates = {};
  for (const [key, value] of Object.entries(rawTemplates)) {
    if (typeof value === 'string') templates[String(key)] = value;
  }
  return {
    tenantId: String(payload?.tenantId || ''),
    templates,
    thankYou: String(payload?.thankYou || ''),
    placeholders: Array.isArray(payload?.placeholders) ? payload.placeholders.map(String) : [],
    canEdit: Boolean(payload?.canEdit),
    raw: payload,
  };
}

/**
 * Tenant-wide appointment-confirmation templates (per appointment type + a
 * "default" fallback, plus the bot thank-you). GET /api/settings/appointment-confirmation
 * → { tenantId, templates: {type: body, default: body}, thankYou, placeholders, canEdit }
 */
export async function fetchAppointmentConfirmationSettings({ token } = {}) {
  const payload = await apiRequest('/api/settings/appointment-confirmation', {
    method: 'GET',
    headers: createAuthHeaders(token),
  });
  return normalizeAppointmentConfirmationPayload(payload);
}

/**
 * Update the appointment-confirmation templates. Partial: only the provided keys
 * are written. PUT /api/settings/appointment-confirmation body { templates?, thankYou? }
 * Requires the settings permission (403 otherwise).
 */
export async function putAppointmentConfirmationSettings({ token, templates, thankYou } = {}) {
  const body = {};
  if (templates && typeof templates === 'object' && !Array.isArray(templates)) {
    body.templates = templates;
  }
  if (typeof thankYou === 'string') {
    body.thankYou = thankYou;
  }
  if (!('templates' in body) && !('thankYou' in body)) {
    throw new ApiError('no settings provided', 400);
  }
  const payload = await apiRequest('/api/settings/appointment-confirmation', {
    method: 'PUT',
    headers: createAuthHeaders(token, { 'Content-Type': 'application/json' }),
    body: JSON.stringify(body),
  });
  return normalizeAppointmentConfirmationPayload(payload);
}

/**
 * Tenant-wide clinic visibility per UI surface (Settings → Clinic visibility). Readable by
 * every active employee. GET /api/settings/clinic-visibility
 * → { clinics: [{id, name, shortName, address, hiddenOn: [surface…]}], surfaces: [{key,label}],
 *    canEdit } — only hidden pairs are stored; a clinic absent from `hidden` shows everywhere.
 * 404 on a backend that predates the feature (the sub-page renders read-only).
 */
export async function fetchClinicVisibility({ token } = {}) {
  const payload = await apiRequest('/api/settings/clinic-visibility', {
    method: 'GET',
    headers: createAuthHeaders(token),
  });
  return normalizeClinicVisibilityPayload(payload);
}

/**
 * Replace the hidden set. PUT /api/settings/clinic-visibility body { hidden: { tenantId:
 * [surface, …] } } (unknown surfaces are dropped server-side, never rejected). Requires the
 * settings permission (403 otherwise); answers the GET shape and broadcasts clinics_changed
 * on the work-plan feed.
 */
export async function putClinicVisibility({ token, hidden } = {}) {
  const payload = await apiRequest('/api/settings/clinic-visibility', {
    method: 'PUT',
    headers: createAuthHeaders(token, { 'Content-Type': 'application/json' }),
    body: JSON.stringify(hiddenMapToBody(hidden)),
  });
  return normalizeClinicVisibilityPayload(payload);
}
